import type { SelectChangeEvent } from '@mui/material';
import { Box, CircularProgress, Container, MenuItem, Stack, Typography } from '@mui/material';
import type { FC } from 'react';
import { Select } from '~/components/atoms/Select';
import { useDashboards } from '~/hooks/useDashboards';
import type { ReportsBannerProps } from './ReportsBanner.types';

export const ReportsBanner: FC<ReportsBannerProps> = ({ selectedDashboardId, onSelect }) => {
  const { dashboards, isLoading: dashboardsLoading } = useDashboards();

  // When the QuickSight FF is enabled, we pull the available dashboards from warehouseApi.listDashboards
  const currentDashboard = dashboards.find((d) => d.id === selectedDashboardId);

  // Select value includes name and id (e.g. "Returns|abc123") for Snowplow tracking
  const selectValue = currentDashboard ? `${currentDashboard.name}|${currentDashboard.id}` : '';

  const handleDashboardChange = (e: SelectChangeEvent<string>) => {
    const value = e.target.value;

    // Reset to reports grid view when "Select a report" is chosen
    if (value === '') {
      onSelect?.(null);
    } else {
      // Extract id from tracking value format "Name|id"
      const [, id] = value.split('|');
      onSelect?.(id);
    }
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        bgcolor: '#F9FAFB',
        py: { xs: 4, md: 6 },
      }}
    >
      <Container>
        <Stack
          sx={{
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
          }}
        >
          <Stack sx={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}>
            <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
              Select Report
            </Typography>

            {dashboardsLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Select
                id="reports-banner-select"
                value={selectValue}
                onChange={handleDashboardChange}
                displayEmpty
                size="small"
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">Select a report</MenuItem>
                {dashboards.map((dashboard) => (
                  <MenuItem key={dashboard.id} value={`${dashboard.name}|${dashboard.id}`}>
                    {dashboard.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};
